import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Star, Sparkles, Compass, Orbit, Hash, Activity, ChevronDown, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { BRAND, IMAGES, SERVICES, WHY_CHOOSE, ZODIAC } from "@/lib/content";
import { toast } from "sonner";
import HoroscopeResult from "@/components/HoroscopeResult";
import { Reveal, RevealLines, easeLux, stagger, item } from "@/lib/motion";
import CTABand from "@/components/CTABand";
import ZodiacWheel from "@/components/ZodiacWheel";
import VastuCompass from "@/components/VastuCompass";
const SERVICE_ICONS = { "scientific-vastu": Compass, astrology: Orbit, numerology: Hash, "energy-scanning": Activity };
const LOCATIONS = ["Dubai", "Abu Dhabi", "Sharjah", "Doha", "London", "Mumbai", "Riyadh"];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[720px] overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <div className="absolute inset-0 hidden bg-cover bg-center sm:block" style={{ backgroundImage: `url(/hero-komel.jpg)` }} />
        <div className="absolute inset-0 overflow-hidden bg-[#07090b] sm:hidden">
          <img src="/profile.png" alt="" className="absolute inset-0 h-full w-full object-contain object-[right_top%]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07090b] via-[#07090b]/75 to-transparent" />
        </div>
      </motion.div>

      {/* Astrology wheel spinning behind Komel + name badge */}
      <motion.div
        style={{ y }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, ease: easeLux, delay: 0.5 }}
        className="absolute inset-0 z-10 pointer-events-none"
      >
        <ZodiacWheel className="absolute top-[8%] left-[60%] -translate-x-1/2 w-[440px] h-[440px] sm:w-[560px] sm:h-[560px] lg:w-[680px] lg:h-[680px] opacity-0 hidden" />
        <span className="absolute top-[20%] left-[60%] -translate-x-1/2 h-72 w-72 sm:h-96 sm:w-96 rounded-full hidden" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.20), rgba(16,185,129,0.10) 50%, transparent 72%)" }} />
        <div className="absolute bottom-24 right-8 md:right-16 z-20 glass rounded-full px-4 py-2 flex items-center gap-2 whitespace-nowrap pointer-events-auto">
          <span className="h-2 w-2 rounded-full bg-primary-green animate-pulse" />
          <span className="text-sm text-white font-medium">{BRAND.consultant}</span>
          <span className="text-xs text-white hidden sm:inline">· Founder</span>
        </div>
      </motion.div>

      {/* Left content */}
      <div className="relative h-full mx-auto max-w-7xl px-6 md:px-8 flex items-center z-20">
        <motion.div style={{ opacity: fade }} className="max-w-xl">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1, duration: 0.9 }}
            className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-gold" />
            <span className="overline text-gold">Scientific Vastu · Astrology · Numerology</span>
          </motion.div>
          <h1 className="font-display text-[12vw] sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white drop-shadow-[0_4px_30px_rgba(0,0,0,0.6)]">
            <RevealLines lines={["Where Science", <>Meets <span className="text-gold italic">Sacred Energy</span></>, "in the Heart of Dubai"]} startDelay={0.3} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 1, ease: easeLux }}
            className="mt-7 max-w-md text-lg text-[#CBD5E1] leading-relaxed">
            Premium consultancy by <span className="text-white font-medium">{BRAND.consultant}</span> — aligning homes, businesses and industries through measurable energy science, without demolition.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 1, ease: easeLux }}
            className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="hero-cta-book" className="group inline-flex items-center justify-center gap-2 rounded-full bg-primary-green px-8 py-4 font-medium text-white transition-all duration-300 hover:-translate-y-1 hover:bg-[#059669]">
              Book a Consultation <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" data-testid="hero-cta-services" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 font-medium text-white transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:text-gold">
              Explore Services
            </Link>
          </motion.div>
        </motion.div>
      </div>


      <motion.div style={{ opacity: fade }} className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-white z-30 pointer-events-none">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}><ChevronDown size={18} /></motion.div>
      </motion.div>
    </section>
  );
}

function Manifesto() {
  const chapters = [
    { n: "01", t: "Measure", d: "We map the energy signature of your space across all 16 directional zones with scientific precision." },
    { n: "02", t: "Diagnose", d: "Aura and chakra scanning reveals blockages, negative zones and imbalances affecting your life." },
    { n: "03", t: "Correct", d: "Targeted, non-invasive remedies — colour, material, placement and geometry — restore positive flow." },
  ];
  return (
    <section className="relative py-24 md:py-36 section-bg" data-testid="manifesto">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline text-gold mb-6">The Scientific Vastu Method</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight">
                Ancient wisdom, engineered for the modern skyline.
              </h2>
              <p className="mt-6 text-white text-lg leading-relaxed">
                Vastu is not superstition. It is the science of energy, direction and space. We bring rigour, measurement and logic to a practice that has guided prosperity for millennia.
              </p>
              <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-gold link-underline">Read Komel&apos;s story <ArrowUpRight size={16} /></Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="group flex gap-6 items-start rounded-2xl border border-white/10 p-7 transition-colors duration-500 hover:border-gold/30 hover:bg-white/[0.02]">
                  <span className="font-display text-4xl text-gold/70 group-hover:text-gold transition-colors">{c.n}</span>
                  <div>
                    <h3 className="font-display text-2xl mb-2">{c.t}</h3>
                    <p className="text-white leading-relaxed">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutSnapshot() {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden" data-testid="about-snapshot">
      <div className="absolute inset-0 radial-glow" />
      <VastuCompass className="absolute -right-40 -top-20 w-[520px] h-[520px] opacity-[0.13] pointer-events-none hidden md:block" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5 relative">
          <Reveal y={60}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-primary-green/20 to-gold/10 blur-2xl" />
              <div className="absolute -top-5 -left-5 h-24 w-24 border-l-2 border-t-2 border-gold/60 rounded-tl-3xl" />
              <div className="absolute -bottom-5 -right-5 h-24 w-24 border-r-2 border-b-2 border-primary-green/60 rounded-br-3xl" />
              <img src="/profile.png" alt="Komel Valliea" className="relative rounded-[1.75rem] w-full object-cover shadow-2xl" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal>
            <p className="overline text-gold mb-6">About the Consultant</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight">
              Meet <span className="text-gold">Komel Valliea</span>
            </h2>
            <p className="mt-6 text-white text-lg leading-relaxed">
              A distinguished Scientific Vastu consultant, astrologer and energy expert based in Dubai. Komel blends spiritual insight with a rigorous, evidence-led approach — helping high-net-worth individuals, corporates and industrialists unlock harmony and growth.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[["12+", "Years Experience"], ["1500+", "Spaces Aligned"], ["4", "Core Disciplines"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-4xl md:text-5xl text-gold">{n}</div>
                  <div className="text-sm text-white mt-1">{l}</div>
                </div>
              ))}
            </div>
            <Link to="/about" data-testid="about-snapshot-cta" className="mt-10 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 transition-all duration-300 hover:border-gold/50 hover:text-gold hover:-translate-y-0.5">
              Discover Her Journey <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ServicesBento() {
  return (
    <section className="relative py-24 md:py-32 section-bg" data-testid="services-bento">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="overline text-gold mb-5">Our Disciplines</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-xl leading-tight">A complete ecosystem of positive energy</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/services" className="inline-flex items-center gap-2 text-gold link-underline">All services <ArrowUpRight size={16} /></Link>
          </Reveal>
        </div>
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[s.key];
            return (
              <motion.div variants={item} key={s.key}>
                <Link to={`/services#${s.key}`} data-testid={`service-card-${s.key}`}
                  className="group relative block h-full overflow-hidden rounded-3xl border border-white/10 transition-all duration-500 hover:border-gold/30 hover:-translate-y-1">
                  <div className="absolute inset-0 bg-cover bg-center opacity-20 transition-all duration-700 group-hover:opacity-30 group-hover:scale-105" style={{ backgroundImage: `url(${s.image})` }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060A17] via-[#060A17]/85 to-[#060A17]/40" />
                  <div className="relative p-9 flex flex-col min-h-[300px]">
                    <div className="flex items-center justify-between">
                      <span className="grid place-items-center h-14 w-14 rounded-2xl bg-primary-green/15 text-primary-green ring-1 ring-primary-green/30">
                        <Icon size={26} />
                      </span>
                      <span className="font-display text-5xl text-white/10">0{i + 1}</span>
                    </div>
                    <h3 className="font-display text-3xl mt-8">{s.title}</h3>
                    <p className="text-gold text-sm mt-2 tracking-wide">{s.tagline}</p>
                    <p className="text-white mt-4 leading-relaxed">{s.desc}</p>
                    <span className="mt-auto pt-6 inline-flex items-center gap-2 text-white/70 group-hover:text-gold transition-colors">Explore <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section className="relative py-24 md:py-32" data-testid="why-choose">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <Reveal>
          <p className="overline text-gold mb-5">Why Choose Us</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-2xl leading-tight">The premium standard for energy consulting</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_CHOOSE.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div className="glass rounded-2xl p-7 h-full transition-transform duration-500 hover:-translate-y-1">
                <span className="grid place-items-center h-11 w-11 rounded-full bg-gold/15 text-gold ring-1 ring-gold/30 mb-6"><Sparkles size={18} /></span>
                <h3 className="font-display text-2xl mb-3">{w.title}</h3>
                <p className="text-white leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocationMarquee() {
  return (
    <div className="relative py-10 border-y border-white/10 section-bg" data-testid="location-marquee">
      <Marquee speed={40} gradient gradientColor="#04060F" gradientWidth={120}>
        {LOCATIONS.concat(LOCATIONS).map((l, i) => (
          <span key={i} className="mx-10 font-display text-4xl md:text-5xl text-white flex items-center gap-10">
            {l} <span className="text-gold text-2xl">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function ProjectsPreview({ projects }) {
  return (
    <section className="relative py-24 md:py-32" data-testid="projects-preview">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="overline text-gold mb-5">Previous Projects</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-xl leading-tight">Transformations across Dubai & beyond</h2>
          </Reveal>
          <Reveal delay={0.1}><Link to="/projects" className="inline-flex items-center gap-2 text-gold link-underline">View all projects <ArrowUpRight size={16} /></Link></Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {projects.slice(0, 3).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <Link to={p.internal_link || `/projects/${p.id}`} className="group block overflow-hidden rounded-3xl border border-white/10 transition-all duration-500 hover:border-gold/30" data-testid={`home-project-${i}`}>
                <div className="relative h-64 overflow-hidden">
                  <img src={p.image} alt={p.image_alt || p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060A17] to-transparent" />
                  <span className="absolute top-4 left-4 rounded-full bg-primary-green/90 px-3 py-1 text-xs font-medium text-white">{p.category}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl">{p.title}</h3>
                  <p className="text-white text-sm mt-1">{p.location}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsMarquee({ testimonials }) {
  if (!testimonials.length) return null;
  return (
    <section className="relative py-24 md:py-32 section-bg" data-testid="testimonials-marquee">
      <div className="mx-auto max-w-7xl px-6 md:px-8 mb-14">
        <Reveal>
          <p className="overline text-gold mb-5">Client Voices</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-2xl leading-tight">Trusted by families, founders & industrialists</h2>
        </Reveal>
      </div>
      <Marquee speed={35} pauseOnHover gradient gradientColor="#04060F" gradientWidth={100}>
        {testimonials.concat(testimonials).map((t, i) => (
          <div key={i} className="mx-4 w-[360px] shrink-0 glass rounded-2xl p-7">
            <div className="flex gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} size={16} className="fill-gold text-gold" />)}</div>
            <p className="text-[#CBD5E1] leading-relaxed line-clamp-4">{`"${t.review}"`}</p>
            <div className="flex items-center gap-3 mt-6">
              <img src={t.photo} alt={t.name} className="h-11 w-11 rounded-full object-cover ring-1 ring-white/20" />
              <div><div className="font-medium text-white">{t.name}</div><div className="text-xs text-white">{t.city}</div></div>
            </div>
          </div>
        ))}
      </Marquee>
    </section>
  );
}

function HomeHoroscope() {
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const current = ZODIAC.find((z) => z.key === selected);

  const pick = async (sign) => {
    setSelected(sign.key);
    setData(null);
    setLoading(true);
    try {
      const res = await api.get(`/horoscope/${sign.key}`);
      setData(res.data);
    } catch (e) {
      toast.error("Horoscope is resting. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative py-24 md:py-32 overflow-hidden" data-testid="home-horoscope">
      <ZodiacWheel className="absolute -right-40 top-10 w-[560px] h-[560px] opacity-[0.08] pointer-events-none" />
      <div className="mx-auto max-w-7xl px-6 md:px-8 relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <Reveal>
            <p className="overline text-gold mb-5">Daily Horoscope</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-xl leading-tight">Find out about your day</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/horoscope" className="inline-flex items-center gap-2 text-gold link-underline">Full horoscope page <ArrowUpRight size={16} /></Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Zodiac selector */}
          <Reveal className="lg:col-span-5">
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 gap-3" data-testid="home-zodiac-grid">
              {ZODIAC.map((z) => (
                <button
                  key={z.key}
                  onClick={() => pick(z)}
                  data-testid={`home-zodiac-${z.key}`}
                  className={`zodiac-orbit group rounded-2xl border py-4 flex flex-col items-center gap-1 ${selected === z.key ? "border-gold bg-gold/10 -translate-y-1" : "border-white/10 hover:border-gold/40 hover:-translate-y-1"}`}
                >
                  <span className={`text-2xl ${selected === z.key ? "text-gold" : "text-white/80 group-hover:text-gold"}`}>{z.symbol}</span>
                  <span className="font-display text-sm">{z.name}</span>
                </button>
              ))}
            </div>
          </Reveal>

          {/* Reading panel */}
          <div className="lg:col-span-7">
            <div className="glass rounded-3xl p-8 md:p-10 min-h-[400px]" data-testid="home-horoscope-panel">
              <AnimatePresence mode="wait">
                {!selected && (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center text-center py-16">
                    <Sparkles className="text-gold mb-5" size={38} />
                    <h3 className="font-display text-3xl">Pick your sign</h3>
                    <p className="text-white mt-3 max-w-sm">Select your zodiac sign to reveal today&apos;s AI-crafted reading, right here.</p>
                  </motion.div>
                )}
                {selected && loading && (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center py-16">
                    <Loader2 className="text-gold animate-spin mb-5" size={38} />
                    <p className="text-white">Consulting the cosmos for {current?.name}...</p>
                  </motion.div>
                )}
                {selected && !loading && data && (
                  <motion.div key="data" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <HoroscopeResult data={data} sign={current} compact />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ToolsTeaser() {
  return (
    <section className="relative py-24 md:py-32 section-bg" data-testid="tools-teaser">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <Reveal delay={0.1}>
          <Link to="/numerology" className="group relative block overflow-hidden rounded-3xl border border-white/10 min-h-[300px] p-9 md:p-12 transition-all duration-500 hover:border-gold/40" data-testid="teaser-numerology">
            <div className="absolute inset-0 bg-cover bg-center opacity-20 group-hover:opacity-30 transition-opacity duration-700" style={{ backgroundImage: `url(${IMAGES.compass})` }} />
            <div className="absolute inset-0 bg-gradient-to-r from-[#060A17] via-[#060A17]/85 to-transparent" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <Hash className="text-gold" size={30} />
                <h3 className="font-display text-4xl mt-6">Numerology Tools</h3>
                <p className="text-white mt-3 leading-relaxed">Instant name & date-of-birth analysis. Reveal your destiny number, lucky colours, personality and more — then connect on WhatsApp for a deeper reading.</p>
              </div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-green px-7 py-4 text-white font-medium whitespace-nowrap transition-transform group-hover:-translate-y-1">Try the calculator <ArrowRight size={16} /></span>
            </div>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
function BlogPreview({ posts }) {
  if (!posts.length) return null;
  return (
    <section className="relative bg-[#060A17] py-24 md:py-32" data-testid="blog-preview">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="overline text-gold mb-5">Insights & Wisdom</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-xl leading-tight">From the journal</h2>
          </Reveal>
          <Reveal delay={0.1}><Link to="/blog" className="inline-flex items-center gap-2 text-gold link-underline">All articles <ArrowUpRight size={16} /></Link></Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.slice(0, 3).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <Link to={p.internal_link || `/blog/${p.slug}`} className="group block overflow-hidden rounded-3xl border border-white/10 h-full transition-all duration-500 hover:border-gold/30" data-testid={`home-blog-${i}`}>
                <div className="relative h-52 overflow-hidden">
                  <img src={p.cover_image} alt={p.image_alt || p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060A17]/80 to-transparent" />
                </div>
                <div className="p-6">
                  <span className="text-xs text-gold tracking-wide">{p.category}</span>
                  <h3 className="font-display text-2xl mt-2 leading-tight group-hover:text-gold transition-colors">{p.title}</h3>
                  <p className="text-white text-sm mt-3 line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative z-10">
      <Hero />
      <HomeHoroscope />
      <Manifesto />
      <AboutSnapshot />
      <ServicesBento />
      <WhyChoose />
      <LocationMarquee />
      <ProjectsPreview projects={projects} />
      <TestimonialsMarquee testimonials={testimonials} />
      <BlogPreview posts={posts} />
      <ToolsTeaser />
      <CTABand />
    </div>
  );
}
